import React, { useMemo } from "react";
import { createContext, useCallback, useEffect, useState, useContext, ReactNode } from "react";
import { IProjectData } from "@src/models/shared/IProjectData";
import { ProjectsResponse } from "@src/models/ProjectsResponse";
import { Logger } from "@src/services/Logger";

export interface ProjectsContextValue {
    projects: IProjectData[];
    loading: boolean;
    reloadProjects: () => void;
    loadProjects: (getProjects: () => Promise<ProjectsResponse>) => void;
}

export const ProjectsContext = createContext<ProjectsContextValue | null>(null);

interface ProjectsProviderProps {
    logger: Logger;
    children: ReactNode;
}

export const ProjectsProvider = (props: ProjectsProviderProps) => {
    const [projects, setProjects] = useState<IProjectData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [savedGetProjects, setSavedGetProjects] = useState<(() => Promise<ProjectsResponse>) | undefined>(undefined);

    const loadProjects = useCallback(
        async (getProjects: () => Promise<ProjectsResponse>) => {
            setLoading(true);

            try {
                const response = await getProjects();
                const projects = response.projects.map((proj) => {
                    const displayName = proj.number ? `${proj.number} - ${proj.name}` : proj.name;
                    return {
                        name: proj.name,
                        number: proj.number,
                        displayName: displayName,
                        nrn: proj.nrn,
                        nixEnabled: proj.nixEnabled,
                        code: proj.code,
                    };
                });

                setSavedGetProjects(() => getProjects);
                setProjects(projects);
            } catch (error) {
                props.logger.error(
                    "Failed to load projects",
                    error instanceof Error ? error : new Error(String(error))
                );
                setProjects([]);
            }
            setLoading(false);
        },
        [props.logger]
    );

    const contextValue = useMemo(
        () => ({
            projects,
            loading,
            reloadProjects: () => savedGetProjects && loadProjects(savedGetProjects),
            loadProjects: (getProjects: () => Promise<ProjectsResponse>) => loadProjects(getProjects),
        }),
        [projects, loading, loadProjects]
    );

    return <ProjectsContext.Provider value={contextValue}>{props.children}</ProjectsContext.Provider>;
};

export const useProjects = (): ProjectsContextValue | null => {
    const context = useContext(ProjectsContext);
    return context;
};
